import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  RiBuilding2Line,
  RiUserLine,
  RiFileTextLine,
  RiBankLine,
  RiHashtag,
  RiMapPinLine,
  RiPhoneLine,
  RiEditLine,
  RiCheckLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiAddLine,
  RiRefreshLine,
  RiErrorWarningLine,
  RiShieldUserLine,
  RiContactsLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { createPortal } from "react-dom";

const BASE_URL = `${process.env.REACT_APP_API_URL}/company`;

const defaultForm = {
  companyName: "",
  ownerName: "",
  pan: "",
  gst: "",
};

const defaultBankForm = {
  accountNumber: "",
  ifscCode: "",
  branchAddress: "",
  branchName: "",
};

const defaultQueryForm = {
  contactPersonName: "",
  phone: "",
};

/* ── Spinner ── */
const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

/* ── Toast ── */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ── Modal ── */
const Modal = ({ title, onClose, children, size = "max-w-2xl" }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${size} mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

/* ── Field ── */
const Field = ({
  label,
  value,
  onChange,
  name,
  type = "text",
  placeholder,
  Icon,
  error,
}) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || label}
        className={`w-full bg-blue-50/60 border rounded-lg ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none transition ${
          error
            ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
            : "border-blue-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        }`}
      />
    </div>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

/* ── Info Row ── */
const InfoRow = ({ Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-blue-50 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-blue-400" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
        {label}
      </p>
      <p className="text-sm font-medium text-slate-700 break-words">
        {value || <span className="text-slate-300 italic">Not set</span>}
      </p>
    </div>
  </div>
);

/* ── Section Card ── */
const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-blue-100 rounded-xl overflow-hidden shadow-sm">
    <div className="px-4 py-3 bg-blue-50/60 border-b border-blue-100 flex items-center gap-2">
      <Icon className="w-4 h-4 text-blue-500" />
      <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {title}
      </span>
    </div>
    <div className="px-4 divide-y divide-blue-50">{children}</div>
  </div>
);

/* ── Company Form Body ── */
const CompanyFormBody = ({ form, onChange, errors }) => {
  const handleChange = (e) => {
    onChange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
          <RiBuilding2Line className="w-3.5 h-3.5" /> Company Information
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Company Name"
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
            Icon={RiBuilding2Line}
            error={errors?.companyName}
          />
          <Field
            label="Owner Name"
            name="ownerName"
            value={form.ownerName}
            onChange={handleChange}
            Icon={RiUserLine}
            error={errors?.ownerName}
          />
          <Field
            label="PAN"
            name="pan"
            value={form.pan}
            onChange={handleChange}
            Icon={RiFileTextLine}
            placeholder="ABCDE1234F"
            error={errors?.pan}
          />
          <Field
            label="GST No."
            name="gst"
            value={form.gst}
            onChange={handleChange}
            Icon={RiShieldUserLine}
            placeholder="22ABCDE1234F1Z5"
            error={errors?.gst}
          />
        </div>
      </div>
    </div>
  );
};

/* ── Bank Account Form Body ── */
const BankFormBody = ({ form, onChange, errors }) => {
  const handleChange = (e) => {
    onChange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
          <RiBankLine className="w-3.5 h-3.5" /> Bank Information
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Account Number"
            name="accountNumber"
            value={form.accountNumber}
            onChange={handleChange}
            Icon={RiHashtag}
            error={errors?.accountNumber}
          />
          <Field
            label="IFSC Code"
            name="ifscCode"
            value={form.ifscCode}
            onChange={handleChange}
            Icon={RiFileTextLine}
            error={errors?.ifscCode}
          />
          <div className="col-span-2">
            <Field
              label="Branch Name"
              name="branchName"
              value={form.branchName}
              onChange={handleChange}
              Icon={RiMapPinLine}
            />
          </div>
          <div className="col-span-2">
            <Field
              label="Branch Address"
              name="branchAddress"
              value={form.branchAddress}
              onChange={handleChange}
              Icon={RiMapPinLine}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Query Form Body ── */
const QueryFormBody = ({ form, onChange, errors }) => {
  const handleChange = (e) => {
    onChange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-blue-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
          <RiContactsLine className="w-3.5 h-3.5" /> Query Contact Information
        </p>
        <div className="grid grid-cols-1 gap-4">
          <Field
            label="Contact Person Name"
            name="contactPersonName"
            value={form.contactPersonName}
            onChange={handleChange}
            Icon={RiUserLine}
            error={errors?.contactPersonName}
          />
          <Field
            label="Phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            Icon={RiPhoneLine}
            error={errors?.phone}
          />
        </div>
      </div>
    </div>
  );
};

/* ── Modal Footer Buttons ── */
const ModalFooter = ({
  onClose,
  onConfirm,
  loading,
  confirmLabel,
  confirmClass,
}) => (
  <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-blue-50">
    <button
      onClick={onClose}
      className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
    >
      <RiCloseLine className="w-4 h-4" /> Cancel
    </button>
    <button
      onClick={onConfirm}
      disabled={loading}
      className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition shadow-md disabled:opacity-60 ${confirmClass || "bg-blue-600 hover:bg-blue-700 shadow-blue-200"}`}
    >
      {loading ? <Spinner /> : <RiCheckLine className="w-4 h-4" />}
      {confirmLabel}
    </button>
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function MyCompany() {
  const [company, setCompany] = useState(null);
  const [banks, setBanks] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [companyForm, setCompanyForm] = useState({ ...defaultForm });
  const [bankForm, setBankForm] = useState({ ...defaultBankForm });
  const [queryForm, setQueryForm] = useState({ ...defaultQueryForm });
  const [editingBankId, setEditingBankId] = useState(null);
  const [editingQueryId, setEditingQueryId] = useState(null);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});

  const notify = (message, type = "success") => setToast({ message, type });
  const clearErrors = () => setErrors({});
  const closeModal = () => {
    setModal(null);
    clearErrors();
  };

  // ── Fetch All ──────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [companyRes, banksRes, queriesRes] = await Promise.all([
        axios.get(`${BASE_URL}/get`),
        axios.get(`${BASE_URL}/bank/all`),
        axios.get(`${BASE_URL}/query/all`),
      ]);
      setCompany(companyRes.data?.data || null);
      setBanks(banksRes.data?.data || []);
      setQueries(queriesRes.data?.data || []);
    } catch {
      notify("Failed to fetch company details", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/bank/all`);
      setBanks(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchQueryDetails = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/query/all`);
      setQueries(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ── Company ────────────────────────────────
  const openCreateCompany = () => {
    clearErrors();
    setCompanyForm({ ...defaultForm });
    setModal("createCompany");
  };

  const openEditCompany = () => {
    clearErrors();
    if (company) {
      setCompanyForm({
        companyName: company.companyName || "",
        ownerName: company.ownerName || "",
        pan: company.pan || "",
        gst: company.gst || "",
      });
    }
    setModal("editCompany");
  };

  const handleCreateCompany = async () => {
    clearErrors();
    const errs = {
      companyName: !companyForm.companyName ? "Required" : "",
      ownerName: !companyForm.ownerName ? "Required" : "",
      pan: !companyForm.pan ? "Required" : "",
      gst: !companyForm.gst ? "Required" : "",
    };
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/create`, companyForm);
      notify("Company created successfully");
      await fetchAll();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to create company",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateCompany = async () => {
    clearErrors();
    const errs = {
      companyName: !companyForm.companyName ? "Required" : "",
      ownerName: !companyForm.ownerName ? "Required" : "",
      pan: !companyForm.pan ? "Required" : "",
      gst: !companyForm.gst ? "Required" : "",
    };
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return;
    }
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/update`, companyForm);
      notify("Company updated successfully");
      const res = await axios.get(`${BASE_URL}/get`);
      setCompany(res.data?.data || null);
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update company",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/delete`);
      notify("Company deleted successfully");
      setCompany(null);
      setBanks([]);
      setQueries([]);
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to delete company",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // ── Bank Accounts ──────────────────────────
  const openAddBank = () => {
    clearErrors();
    setBankForm({ ...defaultBankForm });
    setEditingBankId(null);
    setModal("addBank");
  };

  const openEditBank = (bank) => {
    clearErrors();
    setBankForm({
      accountNumber: bank.accountNumber || "",
      ifscCode: bank.ifscCode || "",
      branchName: bank.branchName || "",
      branchAddress: bank.branchAddress || "",
    });
    setEditingBankId(bank._id);
    setModal("editBank");
  };

  const validateBankForm = () => {
    const errs = {
      accountNumber: !bankForm.accountNumber ? "Required" : "",
      ifscCode: !bankForm.ifscCode ? "Required" : "",
    };
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return false;
    }
    return true;
  };

  const handleAddBank = async () => {
    clearErrors();
    if (!validateBankForm()) return;
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/add-bank`, bankForm);
      notify("Bank account added successfully");
      await fetchBankAccounts();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to add bank account",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateBank = async () => {
    clearErrors();
    if (!validateBankForm()) return;
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/bank/${editingBankId}`, bankForm);
      notify("Bank account updated successfully");
      await fetchBankAccounts();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update bank account",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBank = async (bankId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/remove-bank/${bankId}`);
      notify("Bank account removed successfully");
      await fetchBankAccounts();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to remove bank account",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteBankModal = (bankId) => {
    setEditingBankId(bankId);
    setModal("deleteBank");
  };

  // ── Query Contacts ─────────────────────────
  const openAddQuery = () => {
    clearErrors();
    setQueryForm({ ...defaultQueryForm });
    setEditingQueryId(null);
    setModal("addQuery");
  };

  const openEditQuery = (query) => {
    clearErrors();
    setQueryForm({
      contactPersonName: query.contactPersonName || "",
      phone: query.phone || "",
    });
    setEditingQueryId(query._id);
    setModal("editQuery");
  };

  const validateQueryForm = () => {
    const errs = {
      contactPersonName: !queryForm.contactPersonName ? "Required" : "",
      phone: !queryForm.phone ? "Required" : "",
    };
    if (Object.values(errs).some(Boolean)) {
      setErrors(errs);
      return false;
    }
    return true;
  };

  const handleAddQuery = async () => {
    clearErrors();
    if (!validateQueryForm()) return;
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/add-query`, queryForm);
      notify("Query contact added successfully");
      await fetchQueryDetails();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to add query contact",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateQuery = async () => {
    clearErrors();
    if (!validateQueryForm()) return;
    setActionLoading(true);
    try {
      await axios.put(`${BASE_URL}/query/${editingQueryId}`, queryForm);
      notify("Query contact updated successfully");
      await fetchQueryDetails();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update query contact",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteQuery = async (queryId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/remove-query/${queryId}`);
      notify("Query contact removed successfully");
      await fetchQueryDetails();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to remove query contact",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteQueryModal = (queryId) => {
    setEditingQueryId(queryId);
    setModal("deleteQuery");
  };

  // ══════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* ── Header ── */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiBuilding2Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-900 tracking-tight">
                Company Management
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Complete Profile & Banking
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            {!company && !loading && (
              <button
                onClick={openCreateCompany}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
              >
                <RiAddLine className="w-4 h-4" /> Setup Company
              </button>
            )}

            {company && (
              <div className="flex items-center gap-2">
                <button
                  onClick={openEditCompany}
                  className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                >
                  <RiEditLine className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => setModal("deleteCompany")}
                  className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                >
                  <RiDeleteBinLine className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <div className="max-w-7xl mx-auto px-6 py-8 pb-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-blue-400">
            <AiOutlineLoading3Quarters className="w-8 h-8 animate-spin text-blue-500" />
            <span className="text-sm">Loading company details…</span>
          </div>
        ) : !company ? (
          <div className="flex flex-col items-center justify-center py-32 gap-5">
            <div className="w-20 h-20 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center shadow-inner">
              <RiBuilding2Line className="w-10 h-10 text-blue-300" />
            </div>
            <div className="text-center">
              <p className="text-base font-semibold text-slate-600 mb-1">
                No company profile found
              </p>
              <p className="text-sm text-slate-400 mb-5">
                Set up your company profile to get started.
              </p>
              <button
                onClick={openCreateCompany}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-all shadow-md shadow-blue-200 mx-auto"
              >
                <RiAddLine className="w-4 h-4" /> Setup Company
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Company Hero */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-lg shadow-blue-200">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-3xl flex-shrink-0">
                  {(company.companyName || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-200 mb-1">
                    Company Profile
                  </p>
                  <h2 className="text-2xl font-bold tracking-tight mb-1">
                    {company.companyName}
                  </h2>
                  <p className="text-blue-200">Owner: {company.ownerName}</p>
                </div>
              </div>
            </div>

            {/* Company Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SectionCard title="Company Identity" icon={RiBuilding2Line}>
                <InfoRow
                  Icon={RiUserLine}
                  label="Owner Name"
                  value={company.ownerName}
                />
                <InfoRow
                  Icon={RiFileTextLine}
                  label="PAN"
                  value={company.pan}
                />
                <InfoRow
                  Icon={RiShieldUserLine}
                  label="GST No."
                  value={company.gst}
                />
                <InfoRow
                  Icon={RiFileTextLine}
                  label="Created"
                  value={
                    company.createdAt
                      ? new Date(company.createdAt).toLocaleDateString()
                      : "N/A"
                  }
                />
              </SectionCard>

              <SectionCard title="Record Info" icon={RiFileTextLine}>
                <InfoRow
                  Icon={RiFileTextLine}
                  label="Last Updated"
                  value={
                    company.updatedAt
                      ? new Date(company.updatedAt).toLocaleDateString()
                      : "N/A"
                  }
                />
                <InfoRow
                  Icon={RiBankLine}
                  label="Bank Accounts"
                  value={`${banks.length} registered`}
                />
                <InfoRow
                  Icon={RiContactsLine}
                  label="Query Contacts"
                  value={`${queries.length} registered`}
                />
              </SectionCard>
            </div>

            {/* ── Bank Accounts ── */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <RiBankLine className="w-5 h-5 text-blue-600" />
                  Bank Accounts
                </h3>
                <button
                  onClick={openAddBank}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition shadow-md shadow-blue-200"
                >
                  <RiAddLine className="w-4 h-4" /> Add Bank Account
                </button>
              </div>

              {banks.length === 0 ? (
                <div className="bg-white border border-blue-100 rounded-xl p-8 text-center">
                  <RiBankLine className="w-12 h-12 text-blue-300 mx-auto mb-3" />
                  <p className="text-slate-500">No bank accounts added yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {banks.map((bank) => (
                    <div
                      key={bank._id}
                      className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiBankLine className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditBank(bank)}
                            className="text-blue-500 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition"
                            title="Edit bank account"
                          >
                            <RiEditLine className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteBankModal(bank._id)}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition"
                            title="Delete bank account"
                          >
                            <RiDeleteBinLine className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                            Account Number
                          </p>
                          <p className="text-sm font-semibold text-slate-800">
                            {bank.accountNumber}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                            IFSC Code
                          </p>
                          <p className="text-sm text-slate-700">
                            {bank.ifscCode}
                          </p>
                        </div>
                        {bank.branchName && (
                          <div>
                            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                              Branch Name
                            </p>
                            <p className="text-sm text-slate-700">
                              {bank.branchName}
                            </p>
                          </div>
                        )}
                        {bank.branchAddress && (
                          <div>
                            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                              Branch Address
                            </p>
                            <p className="text-sm text-slate-700">
                              {bank.branchAddress}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Query Contacts ── */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <RiContactsLine className="w-5 h-5 text-blue-600" />
                  Query Contacts
                </h3>
                <button
                  onClick={openAddQuery}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition shadow-md shadow-blue-200"
                >
                  <RiAddLine className="w-4 h-4" /> Add Query Contact
                </button>
              </div>

              {queries.length === 0 ? (
                <div className="bg-white border border-blue-100 rounded-xl p-8 text-center">
                  <RiContactsLine className="w-12 h-12 text-blue-300 mx-auto mb-3" />
                  <p className="text-slate-500">No query contacts added yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {queries.map((query) => (
                    <div
                      key={query._id}
                      className="bg-white border border-blue-100 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiUserLine className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditQuery(query)}
                            className="text-blue-500 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition"
                            title="Edit query contact"
                          >
                            <RiEditLine className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteQueryModal(query._id)}
                            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition"
                            title="Delete query contact"
                          >
                            <RiDeleteBinLine className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                            Contact Person
                          </p>
                          <p className="text-sm font-semibold text-slate-800">
                            {query.contactPersonName}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
                            Phone
                          </p>
                          <p className="text-sm text-slate-700">
                            {query.phone}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          MODALS
      ══════════════════════════════════════ */}

      {/* Create Company */}
      {modal === "createCompany" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Setup Company
            </>
          }
          onClose={closeModal}
        >
          <CompanyFormBody
            form={companyForm}
            onChange={setCompanyForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleCreateCompany}
            loading={actionLoading}
            confirmLabel="Create Company"
          />
        </Modal>
      )}

      {/* Edit Company */}
      {modal === "editCompany" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Company
            </>
          }
          onClose={closeModal}
        >
          <CompanyFormBody
            form={companyForm}
            onChange={setCompanyForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleUpdateCompany}
            loading={actionLoading}
            confirmLabel="Save Changes"
          />
        </Modal>
      )}

      {/* Delete Company */}
      {modal === "deleteCompany" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-2 text-base">
              Delete{" "}
              <span className="text-red-500">{company?.companyName}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. All company data, bank accounts, and
              contacts will be permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteCompany}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Company
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Bank Account */}
      {modal === "addBank" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Bank Account
            </>
          }
          onClose={closeModal}
        >
          <BankFormBody
            form={bankForm}
            onChange={setBankForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleAddBank}
            loading={actionLoading}
            confirmLabel="Add Account"
          />
        </Modal>
      )}

      {/* Edit Bank Account */}
      {modal === "editBank" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Bank Account
            </>
          }
          onClose={closeModal}
        >
          <BankFormBody
            form={bankForm}
            onChange={setBankForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleUpdateBank}
            loading={actionLoading}
            confirmLabel="Save Changes"
          />
        </Modal>
      )}

      {/* Delete Bank Account */}
      {modal === "deleteBank" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-2 text-base">
              Delete Bank Account?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This bank account will be permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={() => handleDeleteBank(editingBankId)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Account
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Query Contact */}
      {modal === "addQuery" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Query Contact
            </>
          }
          onClose={closeModal}
        >
          <QueryFormBody
            form={queryForm}
            onChange={setQueryForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleAddQuery}
            loading={actionLoading}
            confirmLabel="Add Contact"
          />
        </Modal>
      )}

      {/* Edit Query Contact */}
      {modal === "editQuery" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Query
              Contact
            </>
          }
          onClose={closeModal}
        >
          <QueryFormBody
            form={queryForm}
            onChange={setQueryForm}
            errors={errors}
          />
          <ModalFooter
            onClose={closeModal}
            onConfirm={handleUpdateQuery}
            loading={actionLoading}
            confirmLabel="Save Changes"
          />
        </Modal>
      )}

      {/* Delete Query Contact */}
      {modal === "deleteQuery" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-2 text-base">
              Delete Query Contact?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This contact will be permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={() => handleDeleteQuery(editingQueryId)}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Contact
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
